import { z } from "zod"
import { LegacyCompatibleDiscountSchema } from "../service/discount"

export const VatRateSchema = z.union([z.literal(12), z.literal(25)])
export type InvoiceVatRate = z.infer<typeof VatRateSchema>

const ProductRowDataSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  ean: z.string().nullable(),
  articleNumber: z.string().nullable(),
  acquisitionCost: z.number().nullable(),
  title: z.string(),
  price: z.number(),
  quantity: z.number(),
  needsOrdering: z.boolean(),
  expectedDeliveryDate: z.string().nullable(),
  discount: LegacyCompatibleDiscountSchema.nullable(),
})
export type InvoiceProductRow = z.infer<typeof ProductRowDataSchema>

export const InvoiceManualRowPayloadSchema = z.object({
  id: z.string(),
  rowType: z.enum(["part", "wash", "manual", "package", "fee"]),
  label: z.string(),
  comment: z.string(),
  laborPrice: z.number(),
  laborPriceDiscount: LegacyCompatibleDiscountSchema.nullable(),
  products: z.array(ProductRowDataSchema),
})
export type InvoiceManualRowPayload = z.infer<
  typeof InvoiceManualRowPayloadSchema
>

/**
 * The Referens and Etjänstnummer shared by every job on a fakturaunderlag.
 */
export const InvoiceReferencesSchema = z.object({
  reference: z.string().trim().max(500).nullable(),
  eServiceNumber: z.string().trim().max(100).nullable(),
})
export type InvoiceReferences = z.infer<typeof InvoiceReferencesSchema>

export const CreateInvoiceSchema = z.object({
  companyId: z.guid(),
  jobIds: z.array(z.guid()).min(1),
  /** The mechanic's pick for each field whose values differ between jobs. */
  references: InvoiceReferencesSchema.optional(),
})
export type CreateInvoice = z.infer<typeof CreateInvoiceSchema>

export const InvoiceManualRowInputSchema = z.object({
  id: z.guid(),
  vatRate: VatRateSchema,
  payload: InvoiceManualRowPayloadSchema,
  displayOrder: z.number().int().min(0),
})
export type InvoiceManualRowInput = z.infer<typeof InvoiceManualRowInputSchema>

export const InvoiceFreeTextRowInputSchema = z.object({
  id: z.guid(),
  title: z.string().max(255),
  body: z.string().max(10_000),
  displayOrder: z.number().int().min(0),
})
export type InvoiceFreeTextRowInput = z.infer<
  typeof InvoiceFreeTextRowInputSchema
>

export const UpdateInvoiceRowsSchema = z.object({
  manualRows: z.array(InvoiceManualRowInputSchema),
  freeTextRows: z.array(InvoiceFreeTextRowInputSchema),
})
export type UpdateInvoiceRows = z.infer<typeof UpdateInvoiceRowsSchema>

export const UpdateInvoiceJobsSchema = z.object({
  add: z.array(z.guid()).default([]),
  remove: z.array(z.guid()).default([]),
  /** The mechanic's pick for each field whose values differ between jobs. */
  references: InvoiceReferencesSchema.optional(),
})
export type UpdateInvoiceJobs = z.infer<typeof UpdateInvoiceJobsSchema>

export const SetInvoiceInvoicedSchema = z.object({
  invoiced: z.boolean(),
})
export type SetInvoiceInvoiced = z.infer<typeof SetInvoiceInvoicedSchema>

export interface InvoiceManualRow {
  id: string
  vatRate: InvoiceVatRate
  payload: InvoiceManualRowPayload
  displayOrder: number
}

export interface InvoiceFreeTextRow {
  id: string
  title: string
  body: string
  displayOrder: number
}

export interface InvoiceJobSummary {
  id: string
  customerName: string
  bikeDescription: string
  handInDate: string | null
  total: number
  companyContactPersonId: string | null
  reference: string | null
  eServiceNumber: string | null
}

const ProductWithoutBarcodeSchema = z.object({
  id: z.string(),
  title: z.string(),
})

/**
 * The response when a fakturaunderlag can't be sent to Specter because some
 * products lack an article number: per job, and on the invoice's own rows.
 */
export const InvoiceProductsWithoutBarcodeErrorSchema = z.object({
  errorCode: z.literal("PRODUCTS_WITHOUT_BARCODE"),
  message: z.string(),
  productsByJob: z.array(
    z.object({
      jobId: z.string(),
      jobLabel: z.string(),
      products: z.array(ProductWithoutBarcodeSchema),
    }),
  ),
  manualRowProducts: z.array(ProductWithoutBarcodeSchema),
})
export type InvoiceProductsWithoutBarcodeError = z.infer<
  typeof InvoiceProductsWithoutBarcodeErrorSchema
>

export const INVOICE_REFERENCES_DIFFER = "INVOICE_REFERENCES_DIFFER"

/**
 * The response when jobs going onto one fakturaunderlag disagree on Referens
 * or Etjänstnummer: the distinct non-empty values of each field. A field with
 * two or more values needs the mechanic to pick one.
 */
export const InvoiceReferencesConflictErrorSchema = z.object({
  errorCode: z.literal(INVOICE_REFERENCES_DIFFER),
  message: z.string(),
  references: z.array(z.string()),
  eServiceNumbers: z.array(z.string()),
})
export type InvoiceReferencesConflictError = z.infer<
  typeof InvoiceReferencesConflictErrorSchema
>

/** Why an invoice can't be sent to Specter yet. */
export enum SpecterInvoiceBlockReason {
  NO_SPECTER_CUSTOMER_ID = "NO_SPECTER_CUSTOMER_ID",
  NO_LABOR_ARTICLE = "NO_LABOR_ARTICLE",
  NO_JOBS = "NO_JOBS",
  JOBS_HAVE_POS_ORDERS = "JOBS_HAVE_POS_ORDERS",
  JOB_AWAITING_CUSTOMER_ANSWER = "JOB_AWAITING_CUSTOMER_ANSWER",
  SUBMISSION_IN_PROGRESS = "SUBMISSION_IN_PROGRESS",
}

export interface InvoiceSpecterInfo {
  orderId: string | null
  /** Non-null locks the invoice and its jobs. */
  sentAt: string | null
  orderLink: string | null
  blockReason: SpecterInvoiceBlockReason | null
  /** Why the order can't be created yet, ready to show to the mechanic. */
  blockMessage: string | null
}

export interface Invoice {
  id: string
  displayNumber: number
  companyId: string
  storeId: string
  createdAt: string
  updatedAt: string
  invoicedAt: string | null
  jobs: InvoiceJobSummary[]
  manualRows: InvoiceManualRow[]
  freeTextRows: InvoiceFreeTextRow[]
  references: InvoiceReferences & {
    /** The jobs disagree (invoices from before references were shared). */
    differ: boolean
  }
  /** Null when the store doesn't use Specter. */
  specter: InvoiceSpecterInfo | null
}

export interface InvoiceListItem {
  id: string
  displayNumber: number
  jobCount: number
  total: number
  createdAt: string
  invoicedAt: string | null
  sentToSpecterAt: string | null
  contactPersons: {
    id: string
    firstName: string
    lastName: string | null
  }[]
}
